import { z } from 'zod';

/**
 * Proces obsługi sprawy (docs/10-proces-sprawy.md).
 *
 * Zadania procesowe powstają ze stanu sprawy — osobno dla każdego przedmiotu (sprawa, firma, oferta, umowa, zakres,
 * pozycja harmonogramu, protokół). Zadanie kończy się samo, gdy jego przyczyna zniknie (oferta wydana, umowa
 * podpisana…). Kolejka zadania = osoby z wymaganym uprawnieniem; role są tylko wygodnym zestawem uprawnień.
 */

// ── przyjęcie sprawy ────────────────────────────────────────────────────────

export const INTAKE_STATUSES = {
  nowa: 'Nowa — do przejęcia',
  weryfikacja: 'Weryfikacja wstępna',
  do_uzupelnienia: 'Wymaga uzupełnienia',
  zatwierdzona: 'Zatwierdzona — nadano bieg',
  odrzucona: 'Odrzucona',
  duplikat: 'Duplikat',
} as const;
export type IntakeStatus = keyof typeof INTAKE_STATUSES;

// ── status główny i flagi ───────────────────────────────────────────────────

/** Status widoczny na liście spraw — jedna faza procesu; równoległe sprawy pokazują flagi. */
export const CASE_STATUSES = {
  nowa: 'Nowa',
  weryfikacja: 'Weryfikacja wstępna',
  oferta: 'Oferta',
  umowa: 'Umowa',
  realizacja: 'W realizacji',
  weryfikacja_koncowa: 'Weryfikacja końcowa',
  zamknieta: 'Zamknięta',
  odrzucona: 'Odrzucona',
  przegrana: 'Przegrana',
  anulowana: 'Anulowana',
} as const;
export type CaseStatus = keyof typeof CASE_STATUSES;
export const CLOSED_CASE_STATUSES: CaseStatus[] = ['zamknieta', 'odrzucona', 'przegrana', 'anulowana'];

export const CASE_FLAGS = {
  do_przejecia: { label: 'Oczekuje na przejęcie', tone: 'todo' },
  do_uzupelnienia: { label: 'Wymaga uzupełnienia', tone: 'todo' },
  wstrzymana: { label: 'Wstrzymana', tone: 'wait' },
  decyzja_klienta: { label: 'Oczekuje na decyzję klienta', tone: 'wait' },
  podpis: { label: 'Oczekuje na podpis', tone: 'wait' },
  platnosc: { label: 'Oczekuje na płatność', tone: 'wait' },
  platnosc_po_terminie: { label: 'Płatność po terminie', tone: 'late' },
  brak_protokolu: { label: 'Brak protokołu', tone: 'todo' },
  uwagi_protokolu: { label: 'Uwagi w protokole', tone: 'todo' },
  zadanie_po_terminie: { label: 'Zadanie po terminie', tone: 'late' },
  nieobsadzone: { label: 'Zadanie nieobsadzone', tone: 'late' },
  przegrane: { label: 'Wszystkie zakresy przegrane', tone: 'todo' },
  gotowa_do_zamkniecia: { label: 'Gotowa do zamknięcia', tone: 'ok' },
} as const satisfies Record<string, { label: string; tone: 'todo' | 'wait' | 'late' | 'ok' }>;
export type CaseFlag = keyof typeof CASE_FLAGS;

/** Stan zakresu potrzebny do statusu głównego. */
export interface StatusScopeFacts {
  /** etap sprzedaży zakresu */
  salesStage: string;
  /** zakres objęty podpisaną umową */
  signed: boolean;
  /** umowa zakresu w przygotowaniu albo wydana do podpisu */
  contractPending: boolean;
}

export interface StatusFacts {
  closeKind: string | null;
  intake: IntakeStatus;
  scopes: StatusScopeFacts[];
  /** prace i odbiory wszystkich żywych zakresów zakończone — sprawa u opiekuna do weryfikacji końcowej */
  finalVerification: boolean;
}

/**
 * Status główny — reguły sprawdzane po kolei (docs/10 §3.8). Wyliczany jedną funkcją ze stanu sprawy, więc
 * równoległe ścieżki (realizacja i rozliczenia) nie nadpisują go sobie nawzajem. Pokazuje najdalszą fazę;
 * to, co jeszcze trwa obok, pokazują flagi.
 */
export function computeCaseStatus(f: StatusFacts): CaseStatus {
  if (f.closeKind === 'zrealizowana') return 'zamknieta';
  if (f.closeKind === 'anulowana') return 'anulowana';
  if (f.closeKind === 'przegrana') return 'przegrana';
  if (f.closeKind) return 'odrzucona';
  if (f.intake === 'nowa') return 'nowa';
  if (f.intake === 'weryfikacja' || f.intake === 'do_uzupelnienia') return 'weryfikacja';
  const live = f.scopes.filter((s) => s.salesStage !== 'przegrany');
  if (live.length && f.finalVerification) return 'weryfikacja_koncowa';
  if (live.some((s) => s.signed)) return 'realizacja';
  if (live.some((s) => s.salesStage === 'zaakceptowany' || s.contractPending)) return 'umowa';
  return 'oferta';
}

// ── zadania procesowe ───────────────────────────────────────────────────────

export const PROCESS_SUBJECTS = { sprawa: 'Sprawa', firma: 'Firma w sprawie', oferta: 'Wersja oferty', umowa: 'Umowa', zakres: 'Zakres prac', pozycja: 'Pozycja harmonogramu', protokol: 'Protokół', zadanie: 'Praca ekipy', platnosc: 'Płatność online' } as const;
export type ProcessSubject = keyof typeof PROCESS_SUBJECTS;

/** Kto domyślnie dostaje zadanie danego typu. */
export const PROCESS_ASSIGN_RULES = {
  pula: 'Kolejka — przejmuje pierwszy uprawniony',
  opiekun: 'Opiekun sprawy',
  handlowiec: 'Handlowiec prowadzący (bez niego — kolejka)',
  autor: 'Autor dokumentu (bez niego — kolejka)',
  osoba: 'Konkretna osoba',
  podpisujacy: 'Osoba wskazana do podpisu w imieniu firmy',
} as const;
export type ProcessAssignRule = keyof typeof PROCESS_ASSIGN_RULES;

// ── funkcje pracowników w branżach (docs/11) ────────────────────────────────

/**
 * Funkcja pracownika w branżach (tabela user_functions) — kieruje pracę: kto dostaje zadania danej branży.
 * Kolejki (QUEUE_FUNCTIONS) niesie rola; „monter” to branże pracy w terenie. Zakres: wskazane branże albo wszystkie.
 * Branże osoby (planowanie, filtr osób spoza ekip) = branże jej funkcji „monter”.
 */
export const STAFF_FUNCTIONS = {
  opiekun: 'Opiekun spraw',
  handlowiec: 'Handlowiec',
  specjalista_umow: 'Specjalista ds. umów',
  koordynator: 'Koordynator',
  ksiegowy: 'Księgowy',
  /** branże, z których magazynier dostaje zapotrzebowania i powiadomienia (docs/14) */
  magazynier: 'Magazynier',
  monter: 'Monter (wykonawca prac)',
} as const;
export type StaffFunction = keyof typeof STAFF_FUNCTIONS;
export const STAFF_FUNCTION_KEYS = Object.keys(STAFF_FUNCTIONS) as StaffFunction[];

/**
 * Kolejki zadań w sprawach — funkcje, które rola może nieść (Ustawienia → Role → „Zadania w sprawach”).
 * Osoba z taką rolą dostaje zadania kolejki w branżach wskazanych na jej karcie (Dostęp). Funkcja „monter” nie jest
 * kolejką: to branże, w których osoba pracuje w terenie (karta → Dane pracownika → Praca w terenie).
 * Uprawnień funkcje nie dają (od 02.11.2026) — daje je rola.
 */
export const QUEUE_FUNCTIONS = ['opiekun', 'handlowiec', 'specjalista_umow', 'koordynator', 'ksiegowy', 'magazynier'] as const satisfies readonly StaffFunction[];
export type QueueFunction = (typeof QUEUE_FUNCTIONS)[number];
export const isQueueFunction = (fn: string): fn is QueueFunction => (QUEUE_FUNCTIONS as readonly string[]).includes(fn);

/**
 * Uprawnienia wymagane przez zadania kolejki (bez praw decyzji nadawanych tylko przez rolę) — rola z kolejką musi je mieć,
 * inaczej zadania do nikogo nie trafią (ostrzeżenie w edytorze ról).
 */
export function queueTaskPermissions(fn: StaffFunction): string[] {
  return [
    ...new Set(
      Object.values(PROCESS_TASK_TYPES)
        .filter((d) => d.staffFunction === fn && d.phase <= PROCESS_ACTIVE_PHASE && !('roleOnlyPermission' in d && d.roleOnlyPermission))
        .map((d) => d.permission),
    ),
  ];
}

/**
 * Zadanie z kilku branż: kto może je wykonać.
 * podzial — osobne zadanie dla każdej branży; kazda — funkcja we wszystkich branżach zadania;
 * dowolna — funkcja w jednej z nich; wspolne — funkcja w dowolnej branży (zadanie całej sprawy, np. jedna umowa);
 * brak — zadanie bez branży (sprawa) — wystarczy funkcja.
 */
export const TRADE_RULES = {
  podzial: 'Osobne zadanie dla każdej branży',
  kazda: 'Funkcja we wszystkich branżach zadania',
  dowolna: 'Funkcja w jednej z branż zadania',
  wspolne: 'Zadanie wspólne — funkcja w dowolnej branży',
  brak: 'Bez branży (cała sprawa)',
} as const;
export type TradeRule = keyof typeof TRADE_RULES;

/** Czy funkcje osoby (branże; null = wszystkie) kwalifikują do zadania z danymi branżami według zasady. */
export function functionQualifies(userTrades: (string | null)[], taskTrades: string[], rule: TradeRule): boolean {
  if (!userTrades.length) return false;
  if (userTrades.includes(null)) return true;
  if (rule === 'brak' || rule === 'wspolne' || !taskTrades.length) return true;
  const mine = new Set(userTrades as string[]);
  return rule === 'dowolna' ? taskTrades.some((t) => mine.has(t)) : taskTrades.every((t) => mine.has(t));
}

export interface UserFunctionDto {
  id: string;
  function: StaffFunction;
  /** null — wszystkie branże (także dodane później) */
  tradeCode: string | null;
  createdAt: string;
}

export const userFunctionsSchema = z.object({
  functions: z
    .array(z.object({ function: z.enum(STAFF_FUNCTION_KEYS as [StaffFunction, ...StaffFunction[]]), tradeCode: z.string().trim().min(1).max(4).nullable() }))
    .max(60),
});

/** Kto pełni funkcję w branży (Ustawienia → Branże). */
export interface TradeFunctionHolderDto {
  function: StaffFunction;
  users: { id: string; fullName: string; allTrades: boolean; active: boolean }[];
}

export interface ProcessTaskTypeDef {
  name: string;
  description: string;
  /** uprawnienie wymagane do wykonania (i do zobaczenia w kolejce) */
  permission: string;
  subject: ProcessSubject;
  /** reguły dozwolone w ustawieniach (pierwsza — domyślna) */
  rules: ProcessAssignRule[];
  /** domyślny termin w dniach od utworzenia (null — bez terminu) */
  slaDays: number | null;
  /** etap wdrożenia (docs/10 §5) — typy z późniejszych etapów jeszcze nie powstają */
  phase: 1 | 2 | 3 | 4 | 5;
  /** funkcja, która kwalifikuje do zadania (docs/11) */
  staffFunction: StaffFunction;
  /** zasady dla zadań wielobranżowych dozwolone w ustawieniach (pierwsza — domyślna) */
  tradeRules: TradeRule[];
  /**
   * Przypomnienie: zadanie powstaje dopiero, gdy przedmiot czeka dłużej niż termin (slaDays), np. oferta bez decyzji klienta.
   * Sprawa załatwiona od razu (akceptacja, podpis przy wydaniu) nie tworzy zadania. Bez terminu — bez przypomnienia.
   */
  reminder?: boolean;
  /** nazwa kolejki zadania, gdy inna niż nazwa funkcji (np. zatwierdzanie wyjątków — kwalifikuje prawo decyzji, nie funkcja; QA6 P10) */
  queueName?: string;
  /**
   * Uprawnienie typu nadaje tylko rola (prawo decyzji, np. wydanie oferty poniżej progu) — funkcja go nie daje,
   * kieruje jedynie zadanie do osób, które je mają (inaczej każdy handlowiec zatwierdzałby własne rabaty).
   * Do takiego zadania kwalifikuje samo uprawnienie (z dostępem do firmy) — bez wymogu funkcji: zatwierdzającym bywa
   * szef bez funkcji handlowca (P6). Funkcja typu służy wtedy tylko opisowi kolejki.
   */
  roleOnlyPermission?: boolean;
}

/**
 * Branże funkcji osoby, które liczą się dla typu zadania (null = wszystkie). Typ z prawem decyzji nadawanym
 * tylko przez rolę (roleOnlyPermission) — funkcja nie jest wymagana, więc osoba kwalifikuje się we wszystkich branżach.
 */
export function taskFunctionTrades(d: { staffFunction: StaffFunction; roleOnlyPermission?: boolean }, fns: ReadonlyMap<StaffFunction, (string | null)[]> | undefined): (string | null)[] {
  if (d.roleOnlyPermission) return [null];
  return fns?.get(d.staffFunction) ?? [];
}

/**
 * Opis kolejki zadania: nazwa kolejki typu (albo funkcji) i branże zadania — lista zadań, „u kogo” na liście spraw.
 * Typ niezależny od branży (tradeRules tylko „brak”) — bez dopisku branży (QA P13).
 */
export function taskQueueLabel(d: { staffFunction: StaffFunction; queueName?: string; tradeRules?: readonly TradeRule[] }, tradeCodes: string[]): string {
  const tradeless = !!d.tradeRules?.length && d.tradeRules.every((r) => r === 'brak');
  return `${d.queueName ?? STAFF_FUNCTIONS[d.staffFunction]}${tradeCodes.length && !tradeless ? ` · ${tradeCodes.join(', ')}` : ''}`;
}

export const PROCESS_TASK_TYPES = {
  przyjecie: {
    name: 'Przyjęcie sprawy',
    description: 'Nowa sprawa czeka na opiekuna. Kto przejmie zadanie, zostaje opiekunem i w tym samym zadaniu robi weryfikację wstępną: nadaje bieg, kieruje do uzupełnienia albo odrzuca.',
    permission: 'sprawy.przejmowanie',
    subject: 'sprawa',
    rules: ['pula'],
    slaDays: 1,
    phase: 1,
    staffFunction: 'opiekun',
    tradeRules: ['brak'],
  },
  weryfikacja: {
    name: 'Weryfikacja wstępna',
    description: 'Sprawa założona dla wskazanego opiekuna: sprawdź dane klienta, lokalizację i zakres. Uzupełnij braki albo skieruj do uzupełnienia, potem nadaj bieg (wskaż handlowca lub kolejkę) albo odrzuć.',
    permission: 'sprawy.nadanie_biegu',
    subject: 'sprawa',
    rules: ['opiekun'],
    slaDays: 2,
    phase: 1,
    staffFunction: 'opiekun',
    tradeRules: ['brak'],
  },
  oferta: {
    name: 'Przygotowanie i wydanie oferty',
    description: 'Oferta dla branży (zakresu) sprawy — przy kilku branżach osobne zadanie dla każdej. Zadanie kończy się po wydaniu oferty obejmującej wszystkie zakresy.',
    permission: 'oferty.edycja',
    subject: 'firma',
    rules: ['handlowiec', 'pula', 'opiekun', 'osoba'],
    slaDays: 3,
    phase: 1,
    staffFunction: 'handlowiec',
    tradeRules: ['podzial', 'kazda', 'dowolna', 'wspolne'],
  },
  decyzja_klienta: {
    name: 'Decyzja klienta w sprawie oferty',
    description: 'Przypomnienie: oferta wydana dłużej niż termin temu, klient nie zdecydował — skontaktuj się i zarejestruj decyzję: akceptacja, odrzucenie albo potrzebne zmiany.',
    permission: 'oferty.akceptacja_reczna',
    subject: 'oferta',
    rules: ['autor', 'handlowiec', 'pula'],
    slaDays: 3,
    phase: 1,
    staffFunction: 'handlowiec',
    tradeRules: ['dowolna', 'kazda', 'wspolne'],
    reminder: true,
  },
  zatwierdzenie_oferty: {
    name: 'Zatwierdzenie wyjątku w ofercie',
    description:
      'Handlowiec prosi o wydanie oferty z rabatem lub marżą poza progiem (Z-14). Sprawdź przekroczenia i wydaj ofertę z uzasadnieniem albo odrzuć prośbę z komentarzem. Zadanie kończy się samo po wydaniu wersji, odrzuceniu prośby albo usunięciu wersji roboczej. Termin: 1 dzień roboczy.',
    permission: 'oferty.wydanie_ponizej_progu',
    subject: 'oferta',
    rules: ['pula', 'osoba'],
    slaDays: 1,
    phase: 1,
    staffFunction: 'handlowiec',
    tradeRules: ['wspolne'],
    roleOnlyPermission: true,
    queueName: 'Zatwierdzanie wyjątków ofert',
  },
  umowa: {
    name: 'Przygotowanie umowy',
    description: 'Umowa na podstawie zaakceptowanej oferty: klient, firma, zakres, wartość, harmonogram płatności, załączniki. Zadanie kończy się po wydaniu umowy do podpisu.',
    permission: 'umowy.edycja',
    subject: 'firma',
    rules: ['pula', 'handlowiec', 'opiekun', 'osoba'],
    slaDays: 2,
    phase: 1,
    staffFunction: 'specjalista_umow',
    tradeRules: ['wspolne', 'dowolna', 'kazda'],
  },
  podpis_umowy: {
    name: 'Podpisanie umowy',
    description: 'Przypomnienie: umowa wydana do podpisu czeka dłużej niż termin — dopilnuj podpisów i odnotuj podpisany dokument (skan albo podpis elektroniczny).',
    permission: 'umowy.podpis',
    subject: 'umowa',
    rules: ['autor', 'handlowiec', 'pula'],
    slaDays: 7,
    phase: 1,
    staffFunction: 'specjalista_umow',
    tradeRules: ['wspolne', 'dowolna', 'kazda'],
    reminder: true,
  },
  podpis_firmy: {
    name: 'Podpis w imieniu firmy',
    description:
      'Umowa albo aneks wysłane do podpisu elektronicznego czekają na podpis osoby reprezentującej firmę. Termin — ważność wniosku o podpis. Zadanie kończy się samo po podpisie (albo anulowaniu, wygaśnięciu wniosku); zastępca widzi je u siebie, osobę podpisującą zmienia się w umowie („Zmień podpisującego”).',
    permission: 'umowy.reprezentacja',
    subject: 'umowa',
    rules: ['podpisujacy'],
    slaDays: null,
    phase: 1,
    staffFunction: 'specjalista_umow',
    tradeRules: ['brak'],
    // reprezentacja firmy wynika z roli (umowy.reprezentacja), nie z funkcji
    roleOnlyPermission: true,
    queueName: 'Reprezentacja firmy (podpis)',
  },
  planowanie: {
    name: 'Zaplanowanie realizacji',
    description: 'Umowa zakresu podpisana — utwórz prace, przypisz ekipy i terminy.',
    permission: 'planowanie.edycja',
    subject: 'zakres',
    rules: ['pula', 'osoba'],
    slaDays: 5,
    phase: 1,
    staffFunction: 'koordynator',
    tradeRules: ['podzial'],
  },
  rozliczenie: {
    name: 'Rozliczenie pozycji harmonogramu',
    description: 'Wystaw wymagany dokument rozliczeniowy i dopilnuj wpłaty dla pozycji harmonogramu.',
    permission: 'faktury.wystawianie',
    subject: 'pozycja',
    rules: ['pula', 'osoba'],
    slaDays: 3,
    phase: 3,
    staffFunction: 'ksiegowy',
    tradeRules: ['wspolne', 'dowolna', 'kazda'],
  },
  nadplata_online: {
    name: 'Zwrot nadpłaty z płatności online',
    description:
      'Klient zapłacił online (ING Pay) więcej, niż zostało do zapłaty — np. dwa razy tę samą fakturę. Do faktury weszła tylko pozostała kwota. Zwróć nadwyżkę w panelu ING Pay (Transakcje → zwrot) i oznacz nadpłatę jako zwróconą w zakładce Płatności sprawy — zadanie zamknie się samo.',
    permission: 'finanse.wplaty',
    subject: 'platnosc',
    rules: ['pula', 'osoba'],
    slaDays: 2,
    phase: 3,
    staffFunction: 'ksiegowy',
    tradeRules: ['wspolne'],
    queueName: 'Zwroty nadpłat online',
  },
  uwagi_protokolu: {
    name: 'Uwagi w protokole odbioru',
    description: 'Klient zgłosił uwagi — utwórz zadanie poprawkowe albo uznaj uwagi za rozpatrzone.',
    permission: 'planowanie.edycja',
    subject: 'protokol',
    rules: ['pula', 'osoba'],
    slaDays: 3,
    phase: 4,
    staffFunction: 'koordynator',
    tradeRules: ['dowolna', 'kazda', 'wspolne'],
  },
  zmiana_terminu: {
    name: 'Zmiana terminu na prośbę klienta',
    description:
      'Klient poprosił w strefie klienta o zmianę terminu pracy. Sprawdź wolne terminy i przesuń pracę — klient dostanie e-mail z nowym terminem, a zadanie zamknie się samo. Gdy terminu nie da się zmienić, odpowiedz klientowi (odpowiedź zobaczy w strefie i w e-mailu).',
    permission: 'planowanie.edycja',
    subject: 'zadanie',
    rules: ['pula', 'osoba'],
    slaDays: 1,
    phase: 4,
    staffFunction: 'koordynator',
    tradeRules: ['dowolna', 'kazda', 'wspolne'],
  },
  podpis_protokolu: {
    name: 'Podpis protokołu odbioru przez klienta',
    description:
      'Klienta nie było przy odbiorze — protokół wysłano do podpisu elektronicznego albo papierowego. Dopilnuj podpisu: przypomnij (nowy link), zmień drogę na papier albo wgraj skan podpisanego wydruku. Zadanie zamyka się samo po podpisie; dopiero wtedy zakres jest odebrany (faktura końcowa).',
    permission: 'protokoly.wysylka',
    subject: 'protokol',
    rules: ['opiekun', 'pula', 'osoba'],
    slaDays: 3,
    phase: 4,
    staffFunction: 'opiekun',
    tradeRules: ['dowolna', 'kazda', 'wspolne'],
  },
  wstrzymanie: {
    name: 'Koniec wstrzymania sprawy',
    description: 'Przypomnienie: dzień przed datą wznowienia wstrzymanej sprawy (bez daty — po terminie od wstrzymania). Wznów sprawę albo przedłuż wstrzymanie; zastępca opiekuna widzi zadanie u siebie.',
    permission: 'sprawy.wstrzymanie',
    subject: 'sprawa',
    rules: ['opiekun'],
    slaDays: 14,
    phase: 1,
    staffFunction: 'opiekun',
    tradeRules: ['brak'],
    reminder: true,
  },
  weryfikacja_koncowa: {
    name: 'Weryfikacja końcowa',
    description: 'Prace i odbiory zakończone, ale do zamknięcia brakuje czegoś, czym nikt się nie zajmuje (podpisana umowa, wymagany dokument, płatność bez zadania księgowości) — uzupełnij albo potwierdź weryfikację. Kompletna sprawa zamyka się sama.',
    permission: 'sprawy.zamkniecie',
    subject: 'sprawa',
    rules: ['opiekun'],
    slaDays: 3,
    phase: 5,
    staffFunction: 'opiekun',
    tradeRules: ['brak'],
  },
} as const satisfies Record<string, ProcessTaskTypeDef>;
export type ProcessTaskType = keyof typeof PROCESS_TASK_TYPES;
export const PROCESS_TASK_TYPE_KEYS = Object.keys(PROCESS_TASK_TYPES) as ProcessTaskType[];
/** Zrealizowany etap wdrożenia procesu — typy zadań z późniejszych etapów jeszcze nie powstają. */
export const PROCESS_ACTIVE_PHASE = 5;

export const PROCESS_TASK_STATUSES = { otwarte: 'Otwarte', zakonczone: 'Zakończone', anulowane: 'Anulowane' } as const;
export type ProcessTaskStatus = keyof typeof PROCESS_TASK_STATUSES;

export const PROCESS_EVENT_TYPES = {
  utworzenie: 'Utworzono',
  przydzial: 'Przydzielono',
  przejecie: 'Przejęto',
  przekazanie: 'Przekazano',
  zwrot: 'Zwrócono do kolejki',
  zakonczenie: 'Zakończono',
  anulowanie: 'Anulowano',
  wznowienie: 'Otwarto ponownie',
  odlozenie: 'Termin przesunięty',
  wyjatek: 'Przydzielono poza funkcją (wyjątek)',
} as const;
export type ProcessEventType = keyof typeof PROCESS_EVENT_TYPES;

export interface ProcessTaskDto {
  id: string;
  caseId: string;
  caseNumber: string;
  caseTitle: string;
  clientName: string;
  type: ProcessTaskType;
  typeName: string;
  title: string;
  subjectType: ProcessSubject;
  subjectId: string;
  companyId: string | null;
  /** branże zadania (z zakresów przedmiotu) */
  tradeCodes: string[];
  /** w kolejce, a nikt nie spełnia warunków (funkcja, branża, uprawnienie, firma) */
  unstaffed: boolean;
  /** wykonawca nie pełni już funkcji w branżach zadania (zmiana po przejęciu) */
  assigneeOutsideFunction: boolean;
  status: ProcessTaskStatus;
  assigneeId: string | null;
  assigneeName: string | null;
  /** zadanie nieobecnej osoby, które widzę jako zastępca */
  forUserName: string | null;
  createdAt: string;
  dueAt: string | null;
  overdue: boolean;
  completedAt: string | null;
  completedByName: string | null;
  completion: 'auto' | 'reczne' | null;
  comment: string;
  /** kolejka: kto może przejąć (opis uprawnienia) */
  queueLabel: string;
  can: { claim: boolean; release: boolean; assign: boolean; postpone: boolean };
}

export interface ProcessEventDto {
  id: number;
  taskId: string;
  taskTitle: string;
  type: ProcessEventType;
  userName: string | null;
  toUserName: string | null;
  comment: string;
  at: string;
}

/** Wymagany dokument sprawy (kategoria z ustawień branży) i czy jest w sprawie. */
export interface RequiredDocumentDto {
  category: string;
  label: string;
  /** branże, które go wymagają */
  trades: string[];
  count: number;
  ok: boolean;
}

/** Postęp obszaru sprawy (oferta, umowa, realizacja, rozliczenia) — krok 0..max i opis. */
export interface CaseAreaProgressDto {
  step: number;
  max: number;
  label: string;
}

/** Podsumowanie sprawy per firma (docs/10 §4): postęp czterech obszarów. */
export interface CaseCompanySummaryDto {
  companyId: string;
  code: string;
  name: string;
  trades: string[];
  offer: CaseAreaProgressDto;
  contract: CaseAreaProgressDto;
  execution: CaseAreaProgressDto;
  settlement: CaseAreaProgressDto & { paidGr: number | null; totalGr: number | null };
}

export interface CaseSummaryDto {
  companies: CaseCompanySummaryDto[];
  /** moje otwarte zadania w sprawie (także jako zastępca) i do przejęcia przeze mnie */
  myTasks: ProcessTaskDto[];
  /** pozostałe otwarte zadania sprawy (innych osób) */
  otherTasks: ProcessTaskDto[];
  /** co blokuje zamknięcie — gdy prace i odbiory zakończone */
  blockers: string[];
}

export interface CaseProcessDto {
  tasks: ProcessTaskDto[];
  events: ProcessEventDto[];
}

export interface ProcessInboxCountDto {
  mine: number;
  pool: number;
  overdue: number;
}

export interface ProcessCandidateDto {
  id: string;
  fullName: string;
  /** ma uprawnienie i dostęp do firmy, ale nie pełni funkcji w branży zadania — przydział tylko jako wyjątek */
  outsideFunction?: boolean;
}

export const processAssignSchema = z.object({ userId: z.string().uuid(), comment: z.string().trim().max(500).default(''), exception: z.boolean().default(false) });
export type ProcessAssignInput = z.infer<typeof processAssignSchema>;
export const processReleaseSchema = z.object({ comment: z.string().trim().max(500).default('') });
/** Przesunięcie terminu zadania (np. klient prosi o czas do namysłu) — z notatką z kontaktu. */
export const processPostponeSchema = z.object({ until: z.string().date(), comment: z.string().trim().min(3, 'Zanotuj, co ustalono').max(500) });

export const processTaskSettingSchema = z.object({
  type: z.enum(PROCESS_TASK_TYPE_KEYS as [ProcessTaskType, ...ProcessTaskType[]]),
  rule: z.enum(Object.keys(PROCESS_ASSIGN_RULES) as [ProcessAssignRule, ...ProcessAssignRule[]]),
  tradeRule: z.enum(Object.keys(TRADE_RULES) as [TradeRule, ...TradeRule[]]).optional(),
  userId: z.string().uuid().nullable().default(null),
  slaDays: z.number().int().min(1).max(365).nullable(),
});
export type ProcessTaskSettingInput = z.infer<typeof processTaskSettingSchema>;
export const processSettingsSchema = z.object({ types: z.array(processTaskSettingSchema).max(30) });

export interface ProcessTaskSettingDto {
  type: ProcessTaskType;
  name: string;
  description: string;
  permission: string;
  rules: ProcessAssignRule[];
  rule: ProcessAssignRule;
  staffFunction: StaffFunction;
  tradeRules: TradeRule[];
  tradeRule: TradeRule;
  userId: string | null;
  slaDays: number | null;
  active: boolean;
  /** termin = po ilu dniach przypomnieć (zadanie powstaje dopiero wtedy) */
  reminder: boolean;
}

// ── operacje na sprawie ─────────────────────────────────────────────────────

const reason = (min: number, msg: string) => z.string().trim().min(min, msg).max(1000);

/** Nadanie biegu: handlowiec prowadzący (null — oferta do kolejki handlowców). */
export const intakeApproveSchema = z.object({ salesUserId: z.string().uuid().nullable().default(null), comment: z.string().trim().max(1000).default('') });
export type IntakeApproveInput = z.infer<typeof intakeApproveSchema>;
export const intakeMissingSchema = z.object({ note: reason(3, 'Napisz, czego brakuje') });
/** QA6 P24: próba kontaktu z klientem przy uzupełnianiu danych (wpis w dzienniku, termin zadania przesunięty na następny dzień roboczy) */
export const intakeContactAttemptSchema = z.object({ note: z.string().trim().max(1000).default('') }).default({ note: '' });
/** Po tylu nieudanych próbach kontaktu podpowiedź proponuje odrzucenie zgłoszenia. */
export const INTAKE_REJECT_AFTER_ATTEMPTS = 3;
export const intakeRejectSchema = z.object({ reason: reason(3, 'Podaj przyczynę odrzucenia') });
export const intakeDuplicateSchema = z.object({ duplicateOfId: z.string().uuid(), reason: z.string().trim().max(1000).default('') });
/**
 * notifyClient — e-mail do klienta z powodem i datą; przy wstrzymaniu odwołuje też podane mu terminy prac (P34).
 * reason — powód widoczny dla klienta; note — notatka wewnętrzna, tylko w historii sprawy (QA P7; opcjonalna).
 */
export const caseHoldSchema = z.object({ reason: reason(3, 'Podaj powód wstrzymania'), note: z.string().trim().max(1000).default(''), until: z.string().date().nullable().default(null), notifyClient: z.boolean().default(false) });
/** wznowienie — powód (albo notatka wewnętrzna) wymagany (serwer), gdy przed ustaloną datą wznowienia */
export const caseUnholdSchema = z
  .object({ reason: z.string().trim().max(500).default(''), note: z.string().trim().max(1000).default(''), notifyClient: z.boolean().default(false) })
  .default({ reason: '', note: '', notifyClient: false });
export const caseHoldExtendSchema = z.object({ until: z.string().date(), reason: z.string().trim().max(500).default('') });

export interface CaseIntakeDto {
  status: IntakeStatus;
  claimedAt: string | null;
  claimedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  missingNote: string;
  /** QA6 P24: próby kontaktu z klientem od skierowania do uzupełnienia (najnowsze pierwsze) */
  contactAttempts: { at: string; byName: string | null; note: string }[];
  duplicateOf: { id: string; number: string } | null;
  /** zgłoszenia zamknięte jako duplikat tej sprawy */
  duplicates: { id: string; number: string }[];
  /** możliwe duplikaty (P8) — otwarte sprawy tego samego klienta lub adresu w tej samej branży; liczone tylko przed nadaniem biegu */
  possibleDuplicates: PossibleDuplicateCaseDto[];
  salesUser: { id: string; fullName: string } | null;
  can: { claim: boolean; decide: boolean; contact: boolean };
}

/** Otwarta sprawa, która może być tym samym zgłoszeniem (ten sam klient lub adres, wspólna branża). */
export interface PossibleDuplicateCaseDto {
  id: string;
  number: string;
  title: string;
  status: CaseStatus;
  clientName: string;
  address: string;
  /** branże sprawy (kody) */
  trades: string[];
  sameClient: boolean;
  sameSite: boolean;
  createdAt: string;
}

export interface CaseHoldDto {
  reason: string;
  until: string | null;
  at: string;
  byName: string | null;
}

// ── zastępstwa ──────────────────────────────────────────────────────────────

export const substitutionSchema = z
  .object({
    userId: z.string().uuid(),
    deputyId: z.string().uuid(),
    from: z.string().date(),
    to: z.string().date(),
    reason: z.string().trim().max(300).default(''),
    /** QA7 P21: e-mail do klientów otwartych spraw nieobecnego z danymi zastępcy */
    notifyClients: z.boolean().default(false),
    /** P12: tylko te sprawy (spośród otwartych spraw nieobecnego); brak — wszystkie */
    notifyCaseIds: z.array(z.string().uuid()).max(1000).optional(),
  })
  .refine((s) => s.from <= s.to, { message: 'Data końca przed datą początku', path: ['to'] })
  .refine((s) => s.userId !== s.deputyId, { message: 'Zastępca musi być inną osobą', path: ['deputyId'] });
export type SubstitutionInput = z.infer<typeof substitutionSchema>;

export interface SubstitutionDto {
  id: string;
  userId: string;
  userName: string;
  deputyId: string;
  deputyName: string;
  from: string;
  to: string;
  reason: string;
  active: boolean;
}

/** QA7 P21: otwarte sprawy nieobecnego (do powiadomienia klientów o osobie kontaktowej). */
export interface SubstitutionClientNoticeDto {
  cases: number;
  withEmail: number;
  /** P12: otwarte sprawy do wyboru w oknie zastępstwa */
  items: { id: string; number: string; clientName: string; email: string | null }[];
}

/** Czas trwania po polsku: „2 dni 3 godz.”, „45 min”. */
export function formatDuration(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} godz.${min % 60 ? ` ${min % 60} min` : ''}`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'dzień' : 'dni'}${h % 24 ? ` ${h % 24} godz.` : ''}`;
}

// ── tablica opiekuna („Moje sprawy”) ─────────────────────────────────────────

/** Etapy sprawy na osi (status główny → etap). */
export const CASE_PHASES: { key: string; label: string; statuses: CaseStatus[] }[] = [
  { key: 'przyjecie', label: 'Przyjęcie', statuses: ['nowa', 'weryfikacja'] },
  { key: 'oferta', label: 'Oferta', statuses: ['oferta'] },
  { key: 'umowa', label: 'Umowa', statuses: ['umowa'] },
  { key: 'realizacja', label: 'Realizacja', statuses: ['realizacja'] },
  { key: 'zamkniecie', label: 'Weryfikacja końcowa', statuses: ['weryfikacja_koncowa'] },
];
export const casePhaseIndex = (s: CaseStatus) => CASE_PHASES.findIndex((p) => p.statuses.includes(s));

/** Sprawa bez żadnego ruchu dłużej niż tyle dni — „utknęła”. */
export const CASE_STALE_DAYS = 14;

/** Zadanie na tablicy opiekuna — to samo co w „Moich zadaniach” (z działaniami: przejmij, przydziel). */
export type OwnerBoardTaskDto = ProcessTaskDto & { mine: boolean };

export interface OwnerBoardAttentionDto {
  tone: 'late' | 'wait' | 'todo';
  text: string;
}

export interface OwnerBoardCaseDto {
  id: string;
  number: string;
  title: string;
  clientName: string;
  siteAddress: string;
  tradeCodes: string[];
  status: CaseStatus;
  flags: CaseFlag[];
  priority: string;
  /** od kiedy sprawa jest w obecnym statusie */
  statusSince: string;
  /** ostatni ruch w sprawie (zmiana, zadanie, wpis w historii) */
  lastActivityAt: string;
  hold: { reason: string; until: string | null } | null;
  next: { text: string; tone: 'todo' | 'wait' | 'late' | 'ok'; date: string | null } | null;
  tasks: OwnerBoardTaskDto[];
  /** co wymaga uwagi (od najpilniejszego) */
  attention: OwnerBoardAttentionDto[];
  /** utknęła: zadanie po terminie, nieobsadzone, płatność po terminie, koniec wstrzymania albo brak ruchu */
  stuck: boolean;
}

export interface OwnerBoardDto {
  ownerId: string;
  ownerName: string;
  /** może zmieniać opiekuna spraw (sprawy.zmiana_opiekuna) */
  canChangeOwner: boolean;
  cases: OwnerBoardCaseDto[];
}

// ── dokąd prowadzi zadanie ──────────────────────────────────────────────

/**
 * Dopisek do zadania „Rozlicz”, gdy brakuje dokumentu, a firma umowy nie ma włączonej integracji z Fakturownią
 * (CRM nie wystawi faktury) — bez nawiasów, żeby nie mylić z opisem braków „(brak: …)”.
 */
export const SETTLEMENT_NO_INVOICING = 'firma bez Fakturowni — zmień wymagany dokument w rozliczeniu pozycji albo włącz integrację w Ustawieniach → Firmy';

/**
 * Zakładka Finansów dla zadania rozliczenia pozycji harmonogramu (z opisu braków w tytule „… (brak: …)”):
 * dokument do wystawienia (pro forma, faktura końcowa / VAT) → „Do zafakturowania”; sama wpłata do pilnowania
 * albo faktura zaliczkowa (wystawiana do wpłaty — najpierw wpłata) → „Należności”.
 */
export function settlementTaskView(title: string): 'faktury' | 'naleznosci' {
  // po „(brak: …)” może być dopisek „ · …” bez nawiasów (np. przyczyna blokady — firma bez Fakturowni)
  // (dopiski mogą mieć nawiasy, np. „odbiór z uwagami (PO-…)”)
  // P21: jedno zadanie na fakturę końcową (kilka płatności) — wpłatę rejestruje się przy fakturze (Do zafakturowania → nieopłacone)
  if (/ · wpłata przy fakturze\s*$/.test(title)) return 'faktury';
  const brak = /\(brak: (.*?)\)(?: · .*)?\s*$/.exec(title)?.[1]?.toLowerCase() ?? '';
  const payment = /(^|, )(wpłata|dopłata) /.test(brak);
  const advance = brak.includes('zaliczkow');
  const doc = advance || brak.includes('proforma') || brak.includes('końcowa') || brak.includes('vat');
  if (!doc) return 'naleznosci';
  if (advance && payment && !brak.includes('proforma') && !brak.includes('końcowa')) return 'naleznosci';
  return 'faktury';
}

/**
 * Zadanie rozliczenia, w którym brakuje dokumentu wystawianego przed wpłatą (faktura końcowa / VAT, pro forma):
 * pierwszy krok to wystawienie dokumentu (klient bez faktury nie płaci) — tytuł zaczyna się od tego kroku.
 */
export const SETTLEMENT_ISSUE_STEPS = { koncowa: 'Wystaw fakturę końcową', proforma: 'Wystaw pro formę', aneks: 'Wystaw fakturę VAT do aneksu' } as const;

/** QA qa10311123 3.7: krok zadania dla płatności aneksu — dokumentem jest faktura VAT do aneksu (z numerem aneksu). */
export const annexIssueStep = (annexNumber: string | null) => `${SETTLEMENT_ISSUE_STEPS.aneks}${annexNumber ? ` ${annexNumber}` : ''}`;

/** Pierwszy krok zadania rozliczenia („Wystaw fakturę końcową”…) albo null, gdy dokument nie jest pierwszym krokiem. */
export function settlementIssueStep(title: string): string | null {
  const annex = new RegExp(`^(${SETTLEMENT_ISSUE_STEPS.aneks}(?: [^:]+)?):`).exec(title);
  if (annex) return annex[1]!;
  return [SETTLEMENT_ISSUE_STEPS.koncowa, SETTLEMENT_ISSUE_STEPS.proforma].find((s) => title.startsWith(`${s}:`)) ?? null;
}

/** Numer aneksu z kroku „Wystaw fakturę VAT do aneksu <nr>: …” (link do okna faktury do aneksu) albo null. */
export function settlementAnnexNumber(title: string): string | null {
  const step = settlementIssueStep(title);
  return step?.startsWith(`${SETTLEMENT_ISSUE_STEPS.aneks} `) ? step.slice(SETTLEMENT_ISSUE_STEPS.aneks.length + 1) : null;
}

/** Link do miejsca, w którym wykonuje się zadanie: rozliczenia — moduł Finanse (z filtrem sprawy), pozostałe — sprawa. */
export function processTaskLink(t: { type: string; caseId: string; caseNumber: string; title: string; subjectType?: string; subjectId?: string }): string {
  if (t.type === 'rozliczenie') {
    // QA qa10311123 3.7: płatność aneksu — od razu okno faktury VAT do aneksu
    const annex = settlementAnnexNumber(t.title);
    return `/finanse?widok=${settlementTaskView(t.title)}&sprawa=${encodeURIComponent(t.caseNumber)}${annex ? `&aneks=${encodeURIComponent(annex)}` : ''}`;
  }
  // zaplanowanie realizacji — okno „Zaplanuj realizację” w sprawie (wybór branż, potem termin)
  if (t.type === 'planowanie') return `/sprawy/${t.caseId}?realizacja=1${t.subjectType === 'zakres' && t.subjectId ? `&zakres=${t.subjectId}` : ''}`;
  // zatwierdzenie wyjątku — od razu oferta (przedmiot: oferta, wersja robocza otwiera się domyślnie)
  if (t.type === 'zatwierdzenie_oferty' && t.subjectId) return `/oferty/${t.subjectId}`;
  // podpis w imieniu firmy — umowa / aneks z przyciskiem podpisu (P32); podpis klienta — umowa (wysyłka, papier, e-podpis)
  if ((t.type === 'podpis_firmy' || t.type === 'podpis_umowy') && t.subjectId) return `/umowy/${t.subjectId}`;
  // decyzja klienta — wydana oferta
  if (t.type === 'decyzja_klienta' && t.subjectId) return `/oferty/${t.subjectId}`;
  // przygotowanie umowy (przedmiot: firma albo zakres) — zakładka Umowy z oknem „Przygotuj umowy”;
  // gdy umowa robocza obejmuje już wszystkie te zakresy, zakładka sama przechodzi do niej (ContractsTab)
  if (t.type === 'umowa' && t.subjectId) return `/sprawy/${t.caseId}?zakladka=umowy&nowa-umowa=${t.subjectId}`;
  // przygotowanie oferty — zakładka Oferty (wersja robocza albo „Nowa oferta”; nowa wersja po uwagach klienta — z istniejącej oferty)
  if (t.type === 'oferta') return `/sprawy/${t.caseId}?zakladka=oferty`;
  // zmiana terminu na prośbę klienta — praca w sprawie (panel zadania z planowaniem)
  if (t.type === 'zmiana_terminu' && t.subjectId) return `/sprawy/${t.caseId}?zakladka=prace&zadanie=${t.subjectId}`;
  // zwrot nadpłaty online — zakładka Płatności sprawy (lista nadpłat z przyciskiem „Zwrócono”)
  if (t.type === 'nadplata_online') return `/sprawy/${t.caseId}?zakladka=platnosci`;
  return `/sprawy/${t.caseId}?zakladka=zadania`;
}

/**
 * Etykieta bezpośredniego linku „do miejsca wykonania” na karcie zadania (adres: processTaskLink) albo null,
 * gdy zadanie nie ma jednego jednoznacznego miejsca (wtedy wystarcza link do sprawy).
 * Rozliczenia i zmiana terminu mają własne etykiety w widoku listy.
 */
export function processTaskActionLabel(t: { type: string; title: string; subjectId?: string }): string | null {
  switch (t.type) {
    case 'planowanie':
      return 'Zaplanuj realizację';
    case 'umowa':
      return t.subjectId ? (t.title.startsWith('Przygotuj nową wersję umowy') ? 'Przygotuj nową wersję umowy' : 'Przygotuj umowę') : null;
    case 'oferta':
      return t.title.startsWith('Przygotuj nową wersję oferty') || t.title.startsWith('Klient odrzucił ofertę') ? 'Oferty sprawy — nowa wersja' : 'Przygotuj ofertę';
    case 'zatwierdzenie_oferty':
      return t.subjectId ? 'Otwórz ofertę — zatwierdź wyjątek' : null;
    case 'decyzja_klienta':
      return t.subjectId ? 'Otwórz ofertę' : null;
    case 'podpis_umowy':
      return t.subjectId ? (t.title.startsWith('Podpis aneksu') ? 'Otwórz aneks' : 'Otwórz umowę') : null;
    case 'podpis_firmy':
      return t.subjectId ? 'Podpisz w imieniu firmy' : null;
    default:
      return null;
  }
}

/**
 * Napis przycisku zadania w pasku „Co teraz?” (zawsze jakiś — przycisk prowadzi pod processTaskLink):
 * rozliczenie — pierwszy krok („Wystaw fakturę końcową”) albo zakładka Finansów; pozostałe — processTaskActionLabel.
 */
export function processTaskButtonLabel(t: { type: string; title: string; subjectId?: string }): string {
  if (t.type === 'rozliczenie') return settlementIssueStep(t.title) ?? (settlementTaskView(t.title) === 'faktury' ? 'Do zafakturowania' : 'Należności');
  if (t.type === 'zmiana_terminu' && t.subjectId) return 'Otwórz pracę';
  if (t.type === 'nadplata_online') return 'Płatności sprawy';
  return processTaskActionLabel(t) ?? 'Zadania sprawy';
}

/**
 * Tytuł zadania podzielony na krótki nagłówek i szczegóły (pasek „Co teraz?”): „Rozlicz: Zaliczka 30% — UM/… (brak: wpłata …,
 * faktura zaliczkowa (do każdej wpłaty)) · płatne do 11.11” → nagłówek „Rozlicz: Zaliczka 30% — UM/…”, szczegóły
 * „brak: wpłata …, faktura zaliczkowa (do każdej wpłaty) · płatne do 11.11”. Bez „(brak: …)” dzieli na pierwszej „ · ”.
 */
export function splitTaskTitle(title: string): { head: string; detail: string } {
  const i = title.indexOf(' (brak: ');
  if (i >= 0) {
    // nawias braków może zawierać własne nawiasy — szukamy pasującego zamknięcia
    let depth = 0;
    for (let j = i + 1; j < title.length; j++) {
      if (title[j] === '(') depth++;
      else if (title[j] === ')' && --depth === 0) {
        const rest = title.slice(j + 1).replace(/^ · /, '');
        return { head: title.slice(0, i), detail: [title.slice(i + 2, j), rest].filter(Boolean).join(' · ') };
      }
    }
  }
  const k = title.indexOf(' · ');
  return k >= 0 ? { head: title.slice(0, k), detail: title.slice(k + 3) } : { head: title, detail: '' };
}
